import {
  AtomErrors,
  AuthErrors,
  getAtomErrorType,
  getAuthErrorCode,
  getErrorCodeFromUnknown,
  getInternalErrorCode,
  getPrismaErrorCode,
  getUserServiceErrorCode,
  InternalErrors,
  PrismaErrors,
  UserServiceErrors,
} from "@atoms/atom-types";
import type { ApiOperationErrorContext } from "../apiRequestOptions";
import type { NotifyLevel } from "./notifier";

export type UserFacingError = Readonly<{
  level: NotifyLevel;
  message: string;
  description?: string;
  key?: string;
}>;

type UserFacingCatalogEntry = Readonly<{
  level: NotifyLevel;
}>;

type UserFacingOptions = Readonly<{
  defaultMessage?: string;
  keyPrefix?: string;
  operationContext?: ApiOperationErrorContext;
}>;

type UserFacingSuccessOptions = Readonly<{
  successMessage?: string;
  keyPrefix?: string;
  operationContext?: ApiOperationErrorContext;
}>;

const atomErrorCatalog = {
  [AtomErrors.CannotProvideAtom]: {
    level: "error",
  },
  [AtomErrors.AtomIsNotRespawnable]: {
    level: "warning",
  },
  [AtomErrors.InternalError]: {
    level: "error",
  },
  [AtomErrors.FailedToGetLock]: {
    level: "warning",
  },
  [AtomErrors.PersistenceServiceCriticalError]: {
    level: "error",
  },
  [AtomErrors.InsufficientPermission]: {
    level: "warning",
  },
  [AtomErrors.DeadLock]: {
    level: "warning",
  },
  [AtomErrors.LockTimedOut]: {
    level: "warning",
  },
} satisfies Record<AtomErrors, UserFacingCatalogEntry>;

const authErrorCatalog = {
  [AuthErrors.SignUpRequired]: {
    level: "warning",
  },
  [AuthErrors.MigrationRequired]: {
    level: "warning",
  },
  [AuthErrors.EmailNotVerified]: {
    level: "warning",
  },
} satisfies Record<AuthErrors, UserFacingCatalogEntry>;

const userServiceErrorCatalog = {
  [UserServiceErrors.InviteAlreadyAccepted]: {
    level: "info",
  },
  [UserServiceErrors.EmailAlreadyLinkedToDifferentAuthProvider]: {
    level: "warning",
  },
  [UserServiceErrors.EmailLinkRequiresVerifiedMatchingProviderEmail]: {
    level: "warning",
  },
} satisfies Record<UserServiceErrors, UserFacingCatalogEntry>;

const prismaErrorCatalog = {
  [PrismaErrors.StoredProcedureNotFound]: {
    level: "error",
  },
} satisfies Record<PrismaErrors, UserFacingCatalogEntry>;

const internalErrorCatalog = {
  [InternalErrors.NoActingUser]: {
    level: "error",
  },
  [InternalErrors.MissingContextEntry]: {
    level: "error",
  },
  [InternalErrors.InsufficientContext]: {
    level: "error",
  },
  [InternalErrors.CannotSpawnVirtualAtom]: {
    level: "error",
  },
  [InternalErrors.CannotSpawnWithoutLocking]: {
    level: "error",
  },
  [InternalErrors.CannotCommitWithoutLocking]: {
    level: "error",
  },
  [InternalErrors.CannotCommitTwice]: {
    level: "error",
  },
  [InternalErrors.CannotDirectlyChangeVirtualAtoms]: {
    level: "error",
  },
  [InternalErrors.InfiniteLoop]: {
    level: "error",
  },
  [InternalErrors.TryingToCommitFailedTransaction]: {
    level: "error",
  },
  [InternalErrors.ContextOverrideMismatch]: {
    level: "error",
  },
  [InternalErrors.HashMismatch]: {
    level: "warning",
  },
  [InternalErrors.MissingPayload]: {
    level: "error",
  },
  [InternalErrors.ErrorDuringTransformation]: {
    level: "error",
  },
  [InternalErrors.NoLockerSet]: {
    level: "error",
  },
  [InternalErrors.LockerNotFound]: {
    level: "error",
  },
  [InternalErrors.MissingModuleRef]: {
    level: "error",
  },
  [InternalErrors.ContextChanged]: {
    level: "warning",
  },
  [InternalErrors.InsufficientPermissions]: {
    level: "warning",
  },
  [InternalErrors.Persistence]: {
    level: "error",
  },
  [InternalErrors.Configuration]: {
    level: "error",
  },
  [InternalErrors.MissingDependency]: {
    level: "error",
  },
  [InternalErrors.NotFound]: {
    level: "warning",
  },
  [InternalErrors.Validation]: {
    level: "warning",
  },
  [InternalErrors.UnsupportedOperation]: {
    level: "warning",
  },
  [InternalErrors.Unavailable]: {
    level: "warning",
  },
  [InternalErrors.ProviderRequestFailed]: {
    level: "error",
  },
  [InternalErrors.InvariantViolation]: {
    level: "error",
  },
  [InternalErrors.Other]: {
    level: "error",
  },
  [InternalErrors.AtomHasNoRelation]: {
    level: "error",
  },
  [InternalErrors.Unknown]: {
    level: "error",
  },
} satisfies Record<InternalErrors, UserFacingCatalogEntry>;

const GENERIC_USER_FACING_ERROR_MESSAGE =
  "Die Anfrage konnte nicht abgeschlossen werden.";
const ERROR_REFERENCE_PREFIX = "Referenzcode: ";

function formatCatalogEntry(
  entry: UserFacingCatalogEntry,
  keyPrefix: string,
  code?: string,
): UserFacingError {
  return {
    level: entry.level,
    message: GENERIC_USER_FACING_ERROR_MESSAGE,
    description: code ? `${ERROR_REFERENCE_PREFIX}${code}` : undefined,
    key: code ? `${keyPrefix}:${code}` : `${keyPrefix}:known-error`,
  };
}

function formatCatalogCode(
  catalog: Readonly<Record<string, UserFacingCatalogEntry>>,
  catalogCode: string | undefined,
  keyPrefix: string,
  referenceCode = catalogCode,
): UserFacingError | undefined {
  if (!catalogCode) {
    return undefined;
  }

  const entry = catalog[catalogCode];
  return entry
    ? formatCatalogEntry(entry, keyPrefix, referenceCode)
    : undefined;
}

function getPreferredGermanText(
  text?: Readonly<Record<string, string>>,
): string | undefined {
  if (!text) {
    return undefined;
  }

  return text.de ?? Object.values(text)[0];
}

function getOperationContextMessage(
  context: ApiOperationErrorContext,
): string | undefined {
  // Explicit failure message always wins (used by both entity/atom overrides
  // and as the only failure source for custom reducers).
  const explicitFailureMessage = getPreferredGermanText(context.failureMessage);
  if (explicitFailureMessage) {
    return explicitFailureMessage;
  }

  // Reducers describe arbitrary actions — there is no sensible label-based
  // template, so fall back to the generic default if no explicit message exists.
  if (context.targetKind === "reducer") {
    return undefined;
  }

  const label = getPreferredGermanText(context.label);
  if (!label) {
    return undefined;
  }

  switch (context.operation) {
    case "create":
      return `${label} konnte nicht erstellt werden.`;
    case "delete":
      return `${label} konnte nicht gelöscht werden.`;
    case "exists":
      return `${label} konnte nicht gefunden werden.`;
    case "update":
      return `${label} konnte nicht aktualisiert werden.`;
    case "set":
      return `${label} konnte nicht gespeichert werden.`;
    case "addList":
      return `Eintrag für ${label} konnte nicht hinzugefügt werden.`;
    case "removeList":
      return `Eintrag für ${label} konnte nicht entfernt werden.`;
    case "customReducer":
      // Unreachable: reducer kind is handled above. Kept for exhaustiveness.
      return undefined;
  }
}

function getOperationContextSuccessMessage(
  context: ApiOperationErrorContext,
): string | undefined {
  // Explicit success message always wins (the only source for reducers).
  const explicitSuccessMessage = getPreferredGermanText(context.successMessage);
  if (explicitSuccessMessage) {
    return explicitSuccessMessage;
  }

  // Reducers do not produce label-templated success phrases.
  if (context.targetKind === "reducer") {
    return undefined;
  }

  const label = getPreferredGermanText(context.label);
  if (!label) {
    return undefined;
  }

  switch (context.operation) {
    case "create":
      return `${label} erfolgreich erstellt.`;
    case "delete":
      return `${label} erfolgreich gelöscht.`;
    case "exists":
      return `${label} erfolgreich gefunden.`;
    case "update":
      return `${label} erfolgreich aktualisiert.`;
    case "set":
      return `${label} erfolgreich gespeichert.`;
    case "addList":
      return `Eintrag für ${label} erfolgreich hinzugefügt.`;
    case "removeList":
      return `Eintrag für ${label} erfolgreich entfernt.`;
    case "customReducer":
      // Unreachable: reducer kind is handled above. Kept for exhaustiveness.
      return undefined;
  }
}

export function resolveUserFacingError(
  error: unknown,
  options?: UserFacingOptions,
): UserFacingError {
  const defaultMessage =
    options?.defaultMessage ?? "Die Anfrage konnte nicht abgeschlossen werden.";
  const keyPrefix = options?.keyPrefix ?? "err";

  const userServiceCode = getUserServiceErrorCode(error);
  const userServiceError = formatCatalogCode(
    userServiceErrorCatalog,
    userServiceCode,
    keyPrefix,
  );
  if (userServiceError) {
    return userServiceError;
  }

  const authCode = getAuthErrorCode(error);
  const authError = formatCatalogCode(authErrorCatalog, authCode, keyPrefix);
  if (authError) {
    return authError;
  }

  const prismaCode = getPrismaErrorCode(error);
  const prismaError = formatCatalogCode(
    prismaErrorCatalog,
    prismaCode,
    keyPrefix,
  );
  if (prismaError) {
    return prismaError;
  }

  const internalCode = getInternalErrorCode(error);
  const internalError = formatCatalogCode(
    internalErrorCatalog,
    internalCode,
    keyPrefix,
  );
  if (internalError) {
    return internalError;
  }

  const atomErrorType = getAtomErrorType(error);
  if (atomErrorType !== undefined) {
    const exactCode = getErrorCodeFromUnknown(error) ?? atomErrorType;
    const atomError = formatCatalogCode(
      atomErrorCatalog,
      atomErrorType,
      keyPrefix,
      exactCode,
    );
    if (atomError) {
      return atomError;
    }
  }

  const errorCode = getErrorCodeFromUnknown(error);
  const operationMessage = options?.operationContext
    ? getOperationContextMessage(options.operationContext)
    : undefined;
  let key = `${keyPrefix}:unknown-error`;
  if (options?.operationContext) {
    key = `${keyPrefix}:operation-error`;
  }
  if (errorCode) {
    key = `${keyPrefix}:${errorCode}`;
  }

  return {
    level: "error",
    message: operationMessage ?? defaultMessage,
    description: errorCode
      ? `${ERROR_REFERENCE_PREFIX}${errorCode}`
      : undefined,
    key,
  };
}

export function resolveUserFacingSuccess(
  options?: UserFacingSuccessOptions,
): UserFacingError {
  const keyPrefix = options?.keyPrefix ?? "success";
  const message =
    options?.successMessage ??
    (options?.operationContext
      ? getOperationContextSuccessMessage(options.operationContext)
      : undefined) ??
    "Aktion erfolgreich abgeschlossen.";

  return {
    level: "success",
    message,
    key: options?.operationContext
      ? `${keyPrefix}:operation-success`
      : `${keyPrefix}:success`,
  };
}
